import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { FloatingPortal, autoUpdate, flip, offset, shift, useClientPoint, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole, useTransitionStyles, } from '@floating-ui/react';
import { twMerge } from 'tailwind-merge';
export const TOOL_TIP_DEFAULT_DELAY = 500;
/**
 * Renders a non-interactive tooltip component which shows up only on hover,
 * wrap around the element you would like to have the tooltip appear on (reference element).
 *
 * @component
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The content of the tooltip.
 * @param {string} props.text - The text to display in the tooltip.
 * @param {string} props.variant - The variant of the tooltip (e.g., 'light', 'dark').
 * @param {boolean} props.followMouse - Determines if the tooltip should follow the mouse cursor, such as for an overlay.
 * @param {Array<Function>} props.extraMiddleware - Additional middleware functions for the tooltip.
 * @param {Object} props.floatProps - Additional floating properties for the tooltip.
 * @returns {JSX.Element} The rendered tooltip component.
 */
export const Tooltip = ({ children, text, variant, followMouse, extraMiddleware, floatProps, containerClassName, className, delay = TOOL_TIP_DEFAULT_DELAY, placement, }) => {
    const [isOpen, setOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        placement: placement,
        middleware: [
            offset(floatProps?.offset || 5),
            flip({
                crossAxis: true,
                fallbackAxisSideDirection: 'start',
                padding: 5,
                ...floatProps?.flip,
            }),
            shift({ padding: 5, ...floatProps?.shift }),
            ...(extraMiddleware || []),
        ],
    });
    const { isMounted, styles } = useTransitionStyles(context);
    const interactions = [
        ...(followMouse ? [useClientPoint(context)] : []),
        useHover(context, { move: false, delay: { open: delay } }),
        useFocus(context),
        useDismiss(context),
        useRole(context, { role: 'tooltip' }),
    ];
    const { getReferenceProps, getFloatingProps } = useInteractions(interactions);
    return (_jsxs(_Fragment, { children: [_jsx("span", { ref: refs.setReference, ...getReferenceProps(), className: containerClassName, children: children }), isMounted && text && (_jsx(FloatingPortal, { children: _jsx("div", { className: twMerge(`pointer-events-none z-[500] backdrop-blur ${variant === 'light' ? 'bg-sourgum-greyblue-900/25' : 'bg-sourgum-greyblue-900/75'} -left-1/2 top-full w-max max-w-xs rounded-md px-4 py-2 text-xs text-white transition-opacity duration-300 ease-in-out ${!text ? 'hidden' : ''}`, className), ref: refs.setFloating, style: {
                        ...floatingStyles,
                        ...styles,
                    }, ...getFloatingProps(), children: text }) }))] }));
};
//# sourceMappingURL=Tooltip.js.map